/**
 * PathSelector Component
 * 
 * Three-column card layout for choosing authentication path:
 * - Sign In (existing users)
 * - Create Account (new users)
 * - Continue as Guest (anonymous submission)
 */

import React from 'react';
import { motion } from 'framer-motion';

const PathSelector = ({ onSelect }) => {
  const paths = [
    {
      id: 'signin',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
          <polyline points="10 17 15 12 10 7" />
          <line x1="15" y1="12" x2="3" y2="12" />
        </svg>
      ),
      title: 'Sign In',
      description: 'Already have an account? Sign in to link your submission to your profile.',
      highlight: 'Higher priority access',
      buttonText: 'Sign In',
    },
    {
      id: 'signup',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <line x1="20" y1="8" x2="20" y2="14" />
          <line x1="23" y1="11" x2="17" y2="11" />
        </svg>
      ),
      title: 'Create Account',
      description: 'New here? Create an account to track your position and get updates.',
      highlight: 'Recommended',
      buttonText: 'Create Account',
      featured: true,
    },
    {
      id: 'guest',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
      title: 'Continue as Guest',
      description: 'Submit your interest without creating an account. You can always sign up later.',
      highlight: null,
      buttonText: 'Continue',
    },
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: 'easeOut',
      },
    }),
  };

  return (
    <div className="path-selector">
      <div className="path-selector__cards">
        {paths.map((path, index) => (
          <motion.div
            key={path.id}
            className={`path-selector__card ${path.featured ? 'path-selector__card--featured' : ''}`}
            custom={index}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
          >
            {path.highlight && (
              <span className={`path-selector__badge ${path.featured ? 'path-selector__badge--featured' : ''}`}>
                {path.highlight}
              </span>
            )}
            
            <div className="path-selector__icon">
              {path.icon}
            </div>
            
            <h3 className="path-selector__title">{path.title}</h3>
            <p className="path-selector__description">{path.description}</p>
            
            <button
              className={`path-selector__button ${path.featured ? 'path-selector__button--primary' : ''}`}
              onClick={() => onSelect(path.id)}
            >
              {path.buttonText}
              <span className="path-selector__arrow">→</span>
            </button>
          </motion.div>
        ))}
      </div>
      
      <p className="path-selector__footnote">
        All submissions are valued equally — accounts just help us keep you updated.
      </p>
    </div>
  );
};

export default PathSelector;
